import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchProducts } from "../../redux/slices/productSlice";
import { createStockAdjustmentThunk } from "../../redux/slices/stockSlice";
import { getApiErrorMessage } from "../../utils/apiError";

const today = () => new Date().toISOString().split("T")[0];

const ADJUSTMENT_TYPES = [
  { value: "Add", label: "Add (Increase Stock)" },
  { value: "Remove", label: "Remove (Decrease Stock)" },
  { value: "Damage", label: "Damage / Write-off" },
];

const EMPTY = { productID: "", currentQty: 0, adjustmentType: "Add", quantity: "", reason: "", adjustmentDate: today() };

const StockAdjustmentModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const products = useSelector((s) => s.products.list);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const productOptions = products
    .filter((p) => p.status === "Active")
    .map((p) => ({ value: p.productID || p.id, label: `${p.itemName} (Stock: ${p.currentStock || 0})`, currentQty: p.currentStock || 0 }));

  useEffect(() => { dispatch(fetchProducts()); }, [dispatch]);
  useEffect(() => { if (show) setForm({ ...EMPTY, adjustmentDate: today() }); }, [show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.productID || !form.quantity || !form.adjustmentDate) {
      Swal.fire("Required", "Product, Quantity and Date are required.", "warning"); return;
    }
    setSaving(true);
    try {
      await dispatch(createStockAdjustmentThunk(form)).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Stock adjusted successfully", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save stock adjustment."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title"><h4>Stock Adjustment</h4></div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Product <span className="text-danger">*</span></label>
                    <Select classNamePrefix="react-select" options={productOptions}
                      value={productOptions.find((o) => o.value === form.productID) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, productID: opt?.value || "", currentQty: opt?.currentQty || 0 }))}
                      placeholder="Select product"
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Adjustment Type <span className="text-danger">*</span></label>
                    <Select classNamePrefix="react-select" options={ADJUSTMENT_TYPES}
                      value={ADJUSTMENT_TYPES.find((o) => o.value === form.adjustmentType) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, adjustmentType: opt?.value || "Add" }))}
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }} />
                  </div>
                  <div className="col-lg-3 mb-3">
                    <label className="form-label">Quantity <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" min={1} value={form.quantity} onChange={set("quantity")} placeholder="0" />
                  </div>
                  <div className="col-lg-3 mb-3">
                    <label className="form-label">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={form.adjustmentDate} onChange={set("adjustmentDate")} />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Reason</label>
                    <textarea className="form-control" rows={2} value={form.reason} onChange={set("reason")} placeholder="Reason for adjustment (optional)" />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : "Adjust Stock"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default StockAdjustmentModal;
